import styled, { createGlobalStyle } from 'styled-components';

import { StyledProps } from '@/comp/StationControlCenter/ControlCenterCompressorWidget/DetailsWidgetSlider/DetailsWidgetSlider.types';
import {
  getLimitedRelativePosition,
  ANIMATION_SPEED,
  getBeforeBackgroundColor,
  getAfterBackgroundColor,
} from '@/comp/StationControlCenter/ControlCenterCompressorWidget/DetailsWidgetSlider/DetailsWidgetSlider.utils';

export const Point = styled.div<StyledProps>`
  position: absolute;
  text-align: center;
  transform: translateX(-50%);
  left: ${({ $value, $min, $max, $addSideGaps }) => getLimitedRelativePosition($value, $min, $max, $addSideGaps)}%;

  .pipe {
    display: inline-block;
    width: ${({ $highlighted }) => ($highlighted ? '3px' : '1px')};
    border-radius: ${({ theme: { token } }) => token.borderRadiusLG}px;
    height: 9px;
    background-color: ${({ theme: { token } }) => token.colorText};
  }

  .target {
    display: inline-block;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 1px solid ${({ theme: { token } }) => token.colorBorderSecondary};
    background-color: ${({ theme: { token } }) => token.colorPrimary};
    margin-top: -1px;
  }

  .target-tooltip {
    background-color: ${({ theme: { token } }) => token.colorPrimary};
  }

  .point-value {
    font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
    color: ${({ theme: { token }, $highlighted }) => ($highlighted ? token.colorText : token.colorTextTertiary)};

    .label {
      font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
    }
  }

  .point-label {
    font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
    color: ${({ theme: { token }, $highlighted }) => ($highlighted ? token.colorText : token.colorTextTertiary)};
  }

  .subsequent-label {
    position: relative;
    margin-top: 25px;
    color: ${({ theme: { token }, $highlighted }) => ($highlighted ? token.colorText : token.colorTextTertiary)};
  }
`;

export const Container = styled.div<StyledProps>`
  height: 100%;
  font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
  border-radius: 2px;

  .title {
    font-size: ${({ theme: { token } }) => token.fontSize}px;
    color: ${({ theme: { token } }) => token.colorTextTertiary};
  }

  .title-wrapper-tier {
    display: flex;
    justify-content: space-between;
  }

  .target-value {
    font-size: ${({ theme: { token } }) => token.fontSize}px;
    color: ${({ theme: { token } }) => token.colorTextTertiary};

    .target-value-value {
      font-size: ${({ theme: { token } }) => token.fontSizeLG}px;
      color: ${({ theme: { token } }) => token.colorPrimary};
      font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
    }
  }

  .target-value-hidden {
    display: none;
  }

  .slider-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: ${({ theme: { token } }) => token.marginXXL}px;
  }

  .slider-container {
    position: relative;
    margin: auto;
    width: ${({ $hasTarget, $addSideGaps }) => ($addSideGaps ? ($hasTarget ? '60%' : '75%') : '90%')};
    height: 100%;
    margin-bottom: ${({ theme: { token } }) => token.marginXS}px;

    .value-container {
      display: flex;
      position: relative;
    }

    .value-arrow-block {
      display: flex;
      position: relative;
      flex-direction: column;
      align-items: center;
      left: ${({ $value, $min, $max, $addSideGaps }) => getLimitedRelativePosition($value, $min, $max, $addSideGaps)}%;
      transform: translateX(-50%);
      transition: all ${ANIMATION_SPEED}s ease-out;

      .triangle {
        transform: rotate(180deg);
        height: 8px;
        width: 12px;
        margin-bottom: ${({ theme: { token } }) => token.marginXXS}px;
      }
    }

    .value {
      display: flex;
      flex-direction: row;
      align-items: flex-end;
      ${({ $value, $min, $max, $addSideGaps, theme: { token } }) =>
        getLimitedRelativePosition($value, $min, $max, $addSideGaps) < 0
          ? `padding-left: ${`${$value}`.length * token.paddingSM}px;`
          : getLimitedRelativePosition($value, $min, $max, $addSideGaps) > 100
          ? `padding-right: ${`${$value}`.length * token.paddingSM}px;`
          : ''};

      .value-content {
        font-size: ${({ theme: { token } }) => token.fontSizeXL}px;
        font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
        color: ${({ theme: { token } }) => token.colorText};
      }

      .units-sign {
        position: relative;
        color: ${({ theme: { token } }) => token.colorTextTertiary};
        font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
        margin-left: ${({ theme: { token } }) => token.marginXXS}px;
        bottom: ${({ theme: { token } }) => token.marginXXS}px;
      }
    }

    .slider {
      height: 6px;
      width: 100%;
      background-color: ${({ theme: { token } }) => token.colorBorderSecondary};
      display: flex;
      justify-content: flex-start;

      .grey-filler {
        flex-grow: 1;
      }

      .filler {
        background-color: ${({ $highlightSlider, theme: { token } }) =>
          $highlightSlider ? token.colorInfoText : token.colorBorder};
        height: 100%;
        width: ${({ $value, $min, $max, $addSideGaps }) =>
          getLimitedRelativePosition($value, $min, $max, $addSideGaps) || 0}%;
        transition: all ${ANIMATION_SPEED}s ease-out;
      }

      &:before {
        border-top-left-radius: ${({ theme: { token } }) => token.borderRadiusLG}px;
        border-bottom-left-radius: ${({ theme: { token } }) => token.borderRadiusLG}px;
        height: 100%;
        content: '';
        background-color: ${({ $value, $min, $max, $highlightSlider, theme: { token } }) =>
          getBeforeBackgroundColor($value, $min, $max, $highlightSlider, token, token.colorInfoText)};
      }

      &:after {
        border-top-right-radius: ${({ theme: { token } }) => token.borderRadiusLG}px;
        border-bottom-right-radius: ${({ theme: { token } }) => token.borderRadiusLG}px;
        margin-right: -10%;
        height: 6px;
        right: 0;
        content: '';
        background-color: ${({ $value, $min, $max, $highlightSlider, theme: { token } }) =>
          getAfterBackgroundColor($value, $min, $max, $highlightSlider, token, token.colorInfoText)};
      }
    }
  }

  .points {
    display: flex;
    justify-content: space-between;
    position: relative;
    top: -10px;
  }
`;

export const TargetTooltipPopoverStyle = createGlobalStyle`
    .ant-popover.target-tooltip {
      .ant-popover-inner {
        padding: ${({ theme: { token } }) => token.paddingXXS}px ${({ theme: { token } }) => token.paddingXS}px;
        background-color: ${({ theme: { token } }) => token.colorPrimary};
        font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
      }
  
      .ant-popover-arrow::before {
        background-color: ${({ theme: { token } }) => token.colorPrimary};
      }
    }
  `;
